import { useAuthStore } from '@common/auth-layer/store/auth.store'
import { hydrateCache } from '@common/core-layer/utils/cache-hydration.utils'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { useLocation } from '@common/location-layer/composables/useLocation'
import { useNuxtApp } from 'nuxt/app'
import { useCoursesAdminPreviewStore } from '@courses/admin-preview-layer/stores/courses-admin-preview.ui-store'
import {
  isCoursesAdminPreviewContext,
  isCoursesAdminPreviewCredential,
  isCoursesAdminPreviewRoute,
} from '@courses/admin-preview-layer/utils/courses-admin-preview-url.utils'
import { hasAdminV2PreviewSession } from '@common/core-layer/utils/admin-preview-token.utils'
import { hasActiveAdminPreviewMarker } from '@common/core-layer/utils/admin-preview-cookie.utils'
import type { LibraryProducts } from '@courses/types-layer/types/library'
import {
  coursesAccessGuardQueryKeys,
  fetchCourseAccessData,
  type CourseAccessData,
  fetchBasicProductInfo,
  type BasicProductInfo,
} from '../services/courses-access-guard.service'
import { isLegacyCoursesLessonPath, isLegacyCoursesLibraryPath } from '../utils/legacy-course-url'
import type { QueryClient } from '@tanstack/vue-query'
import type { Pinia } from 'pinia'

const libraryQueryKeys = {
  list: (locId: string) => ['library', locId, 'list'] as const,
}

const PUBLIC_CHECKOUT_ROUTE_PREFIXES = ['/courses/offers/', '/courses/checkout/', '/courses/upsell-offers/one-click/'] as const

const isPublicCheckoutRoutePath = (path: string): boolean => PUBLIC_CHECKOUT_ROUTE_PREFIXES.some(prefix => path.startsWith(prefix))

/**
 * Courses Access Control Middleware
 *
 * Authentication and access check for course routes.
 * Redirects unauthenticated users to home page.
 * Redirects users without access to checkout page.
 *
 * Flow:
 * 1. Check if route is a courses route (default or communities context)
 * 2. Skip if already on checkout page (prevent redirect loop)
 * 3. Check if user is authenticated
 * 4. Check library cache first — if product is in the user's library, grant access
 * 5. If library cache is absent, defer guard (allow route without access check)
 * 6. If library cache exists but product not found, fall back to access API
 */
export default defineNuxtRouteMiddleware(async to => {
  const isCoursesRoute = to.path.startsWith('/courses') || to.path.includes('/learning/courses/')
  if (!isCoursesRoute) {
    return
  }

  // Public offer and one-click checkout pages are intentionally accessible without learner auth.
  if (isPublicCheckoutRoutePath(to.path)) {
    return
  }

  // Legacy courses URLs have no matching page and would otherwise be treated as
  // unavailable and redirected home. Defer to the route-resolver middleware,
  // which redirects them to their canonical route.
  if (isLegacyCoursesLessonPath(to.path) || isLegacyCoursesLibraryPath(to.path)) {
    return
  }

  if (to.name === 'courses-checkout' || to.name === 'courses-purchase-product') {
    return
  }

  const { CPNavigateTo } = useCPNavigation()
  const redirectToHome = () => {
    return CPNavigateTo({
      name: 'index',
      options: { replace: true, redirectCode: 302 },
    })
  }

  const redirectToCheckoutPage = (productId: string) => {
    return CPNavigateTo(
      {
        name: 'courses-checkout',
        params: { productId },
        options: { replace: true, redirectCode: 302 },
      },
      true
    )
  }

  const nuxtApp = useNuxtApp()
  const env = process.server ? 'SERVER' : 'CLIENT'

  try {
    const pinia = nuxtApp.$pinia as Pinia
    const authStore = useAuthStore(pinia)
    const isAuthenticated = authStore.isLoggedIn

    // Admin preview authenticates via federated remote / native IAM v2, so learner auth + library
    // checks below don't apply. Bypass on credential-in-URL, hydrated store, `acatv2` session, or
    // (SSR) `capm` marker cookie / preview context — checked independently since global middleware
    // order isn't guaranteed and the URL token may be stripped on reload. SSR bypass still requires
    // the client to prove an actual session (`hasAdminV2PreviewSession()` or hydrated store), since
    // `capm` outlives `sessionStorage` and could otherwise bypass into an unauthenticated `/courses`.
    // Scope is top-level /courses only — communities `/learning/courses/` paths are NOT bypassed.
    const adminPreviewStore = useCoursesAdminPreviewStore(pinia)
    const ssrCookieHeader = process.server ? nuxtApp.ssrContext?.event?.node?.req?.headers?.cookie : undefined
    const isAdminPreviewActive =
      isCoursesAdminPreviewCredential(to.path, to.query) ||
      (isCoursesAdminPreviewRoute(to.path) && (adminPreviewStore.isAdminPreview || hasAdminV2PreviewSession())) ||
      (process.server && (isCoursesAdminPreviewContext(to.path, to.query) || hasActiveAdminPreviewMarker(ssrCookieHeader)))
    if (isAdminPreviewActive) {
      return
    }

    const hasCoursesRouteMatch = to.matched.some(record => record.path.startsWith('/courses') || record.path.includes('/learning/courses/'))
    const isUnavailableCoursesRoute = !hasCoursesRouteMatch

    if (!isAuthenticated || isUnavailableCoursesRoute) {
      console.log(`❌ [Courses Access] ${env}: Blocked route (${to.path}), redirecting to home`)
      return redirectToHome()
    }

    const productId = to.params.productId as string | undefined
    if (!productId) {
      return
    }

    const queryClient = nuxtApp.$queryClient as QueryClient | undefined
    if (!queryClient) {
      return
    }

    const { locationId } = useLocation()
    if (!locationId.value) {
      console.log(`❌ [Courses Access] ${env}: No locationId, redirecting to home`)
      return redirectToHome()
    }

    // Library-first access check: uses the same query key as useLibraryQueryStore
    const cachedLibraryProducts = queryClient.getQueryData<LibraryProducts>(libraryQueryKeys.list(locationId.value))

    if (!cachedLibraryProducts) {
      console.log(`⏳ [Courses Access] ${env}: Library cache not ready, deferring guard for ${to.path}`)
    }

    if (cachedLibraryProducts && cachedLibraryProducts.some(product => product.id === productId)) {
      console.log(`✅ [Courses Access] ${env}: Access granted via library cache for ${to.path}`)
      return
    }

    // Product not in library cache — fall back to access API
    const source = 'communities'

    const basicProductInfo = (await hydrateCache({
      queryClient,
      queryKey: coursesAccessGuardQueryKeys.basicProductInfo(productId, locationId.value),
      fetchFn: () => fetchBasicProductInfo({ courseId: productId, locationId: locationId.value }),
    })) as BasicProductInfo | null

    if (!basicProductInfo || !basicProductInfo.locationId) {
      console.log(`❌ [Courses Access] ${env}: Basic product info not found, redirecting to home`)
      return redirectToHome()
    }

    const accessData = (await hydrateCache({
      queryClient,
      queryKey: coursesAccessGuardQueryKeys.productAccess(productId, basicProductInfo.locationId, source),
      fetchFn: () =>
        fetchCourseAccessData({
          courseId: productId,
          locationId: basicProductInfo.locationId,
          source,
        }),
    })) as CourseAccessData | null

    if (accessData?.hasAccess === false) {
      console.log(`❌ [Courses Access] ${env}: Access denied for ${to.path}, redirecting to checkout`)
      return redirectToCheckoutPage(productId)
    }

    console.log(`✅ [Courses Access] ${env}: Access granted for ${to.path}`)
  } catch (error) {
    console.error(`❌ [Courses Access] ${env}: Access control failed:`, error)
    return redirectToHome()
  }
})
